import React, { useEffect, useMemo, useRef, useState } from "react";
import Text from "../../Atomsv2/Text/Text";
import "./KanbanPanel.scss";

// ─── utilidades internas ──────────────────────────────────────────────────────

const normalizeModules = (modules) =>
    (Array.isArray(modules) ? modules : []).map((m) => ({
        ...m,
        items: Array.isArray(m.items) ? m.items : [],
    }));

const moveItem = (modules, dragMeta, dropTarget) => {
    const board = normalizeModules(modules).map((m) => ({
        ...m,
        items: [...m.items],
    }));

    const src = board.find((m) => m.id === dragMeta.moduleId);
    const dst = board.find((m) => m.id === dropTarget.moduleId);
    if (!src || !dst) return null;

    const srcIdx = src.items.findIndex((item) => item.id === dragMeta.itemId);
    if (srcIdx < 0) return null;

    const [item] = src.items.splice(srcIdx, 1);

    let dstIdx = dropTarget.index;
    if (src.id === dst.id && srcIdx < dropTarget.index) {
        dstIdx = Math.max(0, dstIdx - 1);
    }
    dstIdx = Math.max(0, Math.min(dstIdx, dst.items.length));
    dst.items.splice(dstIdx, 0, item);

    return {
        nextBoard: board,
        move: { item, fromModuleId: dragMeta.moduleId, toModuleId: dropTarget.moduleId, toIndex: dstIdx },
    };
};

// ─── componente ──────────────────────────────────────────────────────────────

/**
 * KanbanPanel — panel tipo kanban con drag-and-drop nativo entre columnas.
 *
 * Props:
 *   title        string     Título del tablero (encabezado).
 *   modules      array      Columnas: [{ id, title, items: [{ id, title, description?, tag? }] }]
 *   onChange     function   Callback al mover una tarjeta: (nextModules, movement) =>{}
 *                           movement = { item, fromModuleId, toModuleId, toIndex }
 *   columnHeight number     Altura máxima del cuerpo de cada columna en px. Default: 420.
 *   emptyMessage string     Texto en columnas vacías. Default: "Suelta una tarjeta aqui".
 *   disabled     boolean    Deshabilita el arrastre. Default: false.
 *   showHistory  boolean    Muestra un historial de los últimos movimientos bajo el tablero. Default: false.
 *   showReset    boolean    Muestra un botón para restablecer el tablero al estado inicial. Default: false.
 *   maxHistory   number     Cantidad máxima de entradas en el historial. Default: 5.
 *   resetLabel   string     Texto del botón de restablecimiento. Default: "Restablecer tablero".
 *   className    string     Clase extra para el contenedor raíz.
 */
const KanbanPanel = ({
    title,
    modules = [],
    onChange,
    className = "",
    columnHeight = 420,
    emptyMessage = "Suelta una tarjeta aqui",
    disabled = false,
    showHistory = false,
    showReset   = false,
    maxHistory  = 5,
    resetLabel  = "Restablecer tablero",
}) => {
    const initialRef            = useRef(normalizeModules(modules)); // snapshot al montar
    const [board, setBoard]     = useState(() => normalizeModules(modules));
    const [history, setHistory] = useState([]);
    const [overId, setOverId]   = useState(null);
    const dragMetaRef           = useRef(null);
    const draggingIdRef         = useRef(null);
    const [, forceRender]       = useState(0);

    useEffect(() => {
        setBoard(normalizeModules(modules));
    }, [modules]);

    const handleReset = () => {
        const initial = normalizeModules(initialRef.current);
        setBoard(initial);
        setHistory([]);
        onChange?.(initial, null);
    };

    const totalItems = useMemo(
        () => board.reduce((sum, m) => sum + m.items.length, 0),
        [board]
    );

    // ── handlers drag & drop ──────────────────────────────────────────────────

    const onDragStart = (e, moduleId, itemId) => {
        if (disabled) return;
        dragMetaRef.current   = { moduleId, itemId };
        draggingIdRef.current = itemId;
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", `${moduleId}:${itemId}`);
        forceRender((n) => n + 1);
    };

    const onDragEnd = () => {
        dragMetaRef.current   = null;
        draggingIdRef.current = null;
        setOverId(null);
        forceRender((n) => n + 1);
    };

    // El drop se registra en el body entero de la columna
    const onColumnDragOver = (e, moduleId) => {
        if (disabled || !dragMetaRef.current) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (overId !== moduleId) setOverId(moduleId);
    };

    const onColumnDragLeave = (e) => {
        // Solo limpiar si el cursor salió realmente de la columna
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setOverId(null);
        }
    };

    const onColumnDrop = (e, moduleId, items) => {
        if (disabled || !dragMetaRef.current) return;
        e.preventDefault();
        e.stopPropagation();

        const meta = dragMetaRef.current;
        dragMetaRef.current   = null;
        draggingIdRef.current = null;
        setOverId(null);
        forceRender((n) => n + 1);

        // Calcular índice de inserción por posición Y del mouse
        const cards = Array.from(
            e.currentTarget.querySelectorAll(".kanban-card:not(.is-dragging)")
        );
        let toIndex = items.length;
        for (let i = 0; i < cards.length; i++) {
            const rect = cards[i].getBoundingClientRect();
            if (e.clientY < rect.top + rect.height / 2) {
                toIndex = i;
                break;
            }
        }

        const result = moveItem(board, meta, { moduleId, index: toIndex });
        if (!result) return;

        setBoard(result.nextBoard);

        if (showHistory) {
            // Buscar títulos de columna para mostrarlos en el historial
            const colMap = Object.fromEntries(result.nextBoard.map((m) => [m.id, m.title]));
            const entry  = {
                item:           result.move.item,
                fromModuleId:   result.move.fromModuleId,
                fromTitle:      colMap[result.move.fromModuleId] ?? result.move.fromModuleId,
                toModuleId:     result.move.toModuleId,
                toTitle:        colMap[result.move.toModuleId]   ?? result.move.toModuleId,
            };
            setHistory((prev) => [entry, ...prev].slice(0, maxHistory));
        }

        onChange?.(result.nextBoard, result.move);
    };

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <section className={`KanbanPanel ${className}`.trim()}>
            {(title || totalItems > 0 || showReset) && (
                <header className="kanban-header">
                    <div className="kanban-header-left">
                        {title && <Text size="medium-large" weight={700}>{title}</Text>}
                        <Text size="small" color="var(--gray-dark-500)">{totalItems} elementos</Text>
                    </div>
                    {showReset && (
                        <button className="kanban-reset-btn" onClick={handleReset} type="button">
                            <span className="icon" data-icon="refresh" />
                            {resetLabel}
                        </button>
                    )}
                </header>
            )}

            <div className="kanban-columns" role="list" aria-label={title || "Kanban"}>
                {board.map((module) => (
                    <article key={module.id} className="kanban-column" role="listitem">
                        <div className="kanban-column-head">
                            <Text size="small" weight={700}>{module.title}</Text>
                            <span className="kanban-count">{module.items.length}</span>
                        </div>

                        <div
                            className={`kanban-column-body ${overId === module.id ? "is-over" : ""}`}
                            style={{ maxHeight: `${columnHeight}px` }}
                            onDragOver={(e) => onColumnDragOver(e, module.id)}
                            onDragLeave={onColumnDragLeave}
                            onDrop={(e) => onColumnDrop(e, module.id, module.items)}
                        >
                            {module.items.map((item) => {
                                const isDragged = draggingIdRef.current === item.id;
                                return (
                                    <div
                                        key={item.id}
                                        className={`kanban-card ${isDragged ? "is-dragging" : ""}`}
                                        draggable={!disabled}
                                        onDragStart={(e) => onDragStart(e, module.id, item.id)}
                                        onDragEnd={onDragEnd}
                                        onDragOver={(e) => {
                                            // Acepta el drop sobre la tarjeta misma;
                                            // el evento burbujea al column-body que ejecuta el drop.
                                            if (!dragMetaRef.current) return;
                                            e.preventDefault();
                                        }}
                                    >
                                        <div className="kanban-card-main">
                                            <Text size="extra-small" weight={600}>{item.title}</Text>
                                            {item.description && (
                                                <Text size="extra-small" color="var(--gray-dark-500)">
                                                    {item.description}
                                                </Text>
                                            )}
                                        </div>
                                        {item.tag && (
                                            <span className="kanban-tag">{item.tag}</span>
                                        )}
                                    </div>
                                );
                            })}

                            {module.items.length === 0 && (
                                <div className="kanban-empty">{emptyMessage}</div>
                            )}

                            {/* zona flexible al fondo: amplía el área de drop sin importar cuántas tarjetas haya */}
                            <div className="kanban-column-foot" />
                        </div>
                    </article>
                ))}
            </div>

            {showHistory && history.length > 0 && (
                <div className="kanban-history">
                    <div className="kanban-history-title">
                        <span className="icon" data-icon="history" />
                        <Text size="small" weight={600}>Historial de movimientos</Text>
                    </div>
                    <ul className="kanban-history-list">
                        {history.map((entry, i) => (
                            <li key={i} className="kanban-history-item">
                                <span className="icon" data-icon="drag_indicator" />
                                <Text size="extra-small" className="kanban-history-item-title">
                                    <strong>{entry.item?.title}</strong>
                                </Text>
                                <span className="kanban-history-from">{entry.fromTitle}</span>
                                <span className="icon" data-icon="arrow_forward" />
                                <span className="kanban-history-to">{entry.toTitle}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};

export default KanbanPanel;
